/**
 * <TutorPrepareSessionCard> — un live à préparer, et chacun de ses élèves (organism).
 *
 * La carte d'occurrence de l'ancien `PrepareLiveScreen` : en tête, le jour et l'heure, et l'anneau
 * qui compte les élèves prêts (« 1 / 2 ») ; dessous, une ligne par élève — sa photo ou son
 * initiale, son nom, ses chips (« Programme de révision », « Connecté·e », « Non préparé »), sa
 * classe (« Classe non définie » sans), puis ce qu'il faut choisir pour que Sam démarre avec lui :
 *  - en programme de révision, un CHAPITRE — « Choisir un chapitre » tant qu'il n'y en a pas, le
 *    chapitre choisi sinon (on le touche pour en changer) ;
 *  - sinon, UNE matière, celle de son objectif en tête (« matière habituelle »).
 * Un élève déjà connecté au live ne se prépare plus : sa ligne s'estompe, ses choix se figent.
 * Un élève dont s'occupe un checkpoint de démarrage s'affiche mais ne compte pas.
 *
 * Contrôlée : l'élève en cours d'enregistrement (`updating`) arrive dans la donnée.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { CircularProgress } from "../../atoms/CircularProgress"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { CardList, CardListRow } from "../../molecules/CardList"
import { OptionCard } from "../../molecules/OptionCard"
import { SelectField } from "../../molecules/SelectField"
import { formatDayTime, formatDuration } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UISubject } from "../../../ports/primary/ui/types"
import { type UITutorPrepareOccurrence, type UITutorPrepareStudent } from "../../../ports/primary/ui/types/tutorLives"

const TEXTS = {
    studyProgram: "Programme de révision",
    connected: "Connecté·e",
    notPrepared: "Non préparé",
    noClass: "Classe non définie",
    chooseChapter: "Choisir un chapitre",
    subjectLabel: "Matière pour la préparation avec Sam",
    subjectPlaceholder: "Matière(s) pour la préparation avec Sam",
    usual: "matière habituelle",
    updating: "Mise à jour…",
}

/** L'élève compte dans la préparation — un checkpoint de démarrage s'occupe des autres. */
function countsForPreparation(student: UITutorPrepareStudent): boolean {
    return student.hasCheckpoint !== true
}

/** Son chapitre (en programme) ou sa matière (hors programme) est choisi — `getSessionStats` du legacy. */
function isStudentPrepared(student: UITutorPrepareStudent): boolean {
    return student.hasStudyProgram ? student.chapter != null : student.subjectId != null
}

/** Les élèves prêts et à préparer d'un live. */
export function preparationStats(occurrence: UITutorPrepareOccurrence): { done: number; total: number } {
    const counted = occurrence.students.filter(countsForPreparation)
    return { done: counted.filter(isStudentPrepared).length, total: counted.length }
}

export type TutorPrepareSessionCardProps = {
    occurrence: UITutorPrepareOccurrence
    /** Les matières qu'on peut choisir pour un élève hors programme. */
    subjects: UISubject[]
    /** « Choisir un chapitre » ou le chapitre touché : la boîte des chapitres de cet élève. */
    onChooseChapter?: (student: UITutorPrepareStudent) => void
    onSelectSubject?: (student: UITutorPrepareStudent, subjectId: string | number) => void
    testID?: string
}

export function TutorPrepareSessionCard(props: TutorPrepareSessionCardProps) {
    const { occurrence, subjects, onChooseChapter, onSelectSubject, testID } = props
    const { done, total } = preparationStats(occurrence)

    return (
        <CardList testID={testID}>
            <CardListRow>
                <Row gap="3" align="center" justify="between">
                    <Row gap="2" align="center" style={styles.grow}>
                        <Icon name="signal" size={20} color="role" />
                        <Text size="text-md" weight="bold" color="text-primary" style={styles.grow}>
                            {formatDayTime(occurrence.date, true)}
                        </Text>
                    </Row>
                    <CircularProgress
                        value={total > 0 ? (done / total) * 100 : 0}
                        size={48}
                        strokeWidth={4}
                        color="success"
                        label={`${done}/${total}`}
                    />
                </Row>
            </CardListRow>
            {occurrence.students.map((student) => (
                <CardListRow key={student.id} divided>
                    <StudentPreparation
                        student={student}
                        subjects={subjects}
                        onChooseChapter={onChooseChapter}
                        onSelectSubject={onSelectSubject}
                    />
                </CardListRow>
            ))}
        </CardList>
    )
}

function StudentPreparation(props: {
    student: UITutorPrepareStudent
    subjects: UISubject[]
    onChooseChapter?: (student: UITutorPrepareStudent) => void
    onSelectSubject?: (student: UITutorPrepareStudent, subjectId: string | number) => void
}) {
    const { student, subjects, onChooseChapter, onSelectSubject } = props
    const connected = student.connected === true
    const updating = student.updating === true
    const locked = connected || updating
    const name = [student.firstname, student.lastname].filter(Boolean).join(" ")

    // La matière de son objectif passe en tête de liste et le dit — l'étoile de l'ancien sélecteur.
    const ordered = [...subjects].sort(
        (a, b) => Number(b.id === student.usualSubjectId) - Number(a.id === student.usualSubjectId)
    )
    const subjectLabel = (subject: UISubject) =>
        subject.id === student.usualSubjectId ? `${subject.name} (${TEXTS.usual})` : subject.name

    return (
        <Stack gap="3">
            <Row gap="3" align="center">
                <Avatar uri={student.avatarUri} name={student.firstname} size={44} dimmed={connected} />
                <Stack gap="1" style={styles.grow}>
                    <Text size="text-base" weight="bold" color={connected ? "text-muted" : "text-primary"}>
                        {name}
                    </Text>
                    <Text size="text-sm" color="text-muted">
                        {student.className ?? TEXTS.noClass}
                    </Text>
                    {(student.hasStudyProgram || connected) && (
                        <Row gap="1" style={styles.wrap}>
                            {student.hasStudyProgram && (
                                <Badge text={TEXTS.studyProgram} variant="neutral" iconName="book-open" />
                            )}
                            {connected && <Badge text={TEXTS.connected} variant="success" />}
                            {connected && !isStudentPrepared(student) && (
                                <Badge text={TEXTS.notPrepared} variant="warning-deep" />
                            )}
                        </Row>
                    )}
                </Stack>
            </Row>

            {student.hasStudyProgram ? (
                student.chapter != null ? (
                    <OptionCard
                        label={student.chapter.name}
                        description={[
                            student.chapter.subject?.name,
                            student.chapter.duration != null ? formatDuration(student.chapter.duration) : undefined,
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                        leading={
                            student.chapter.subject != null ? (
                                <SubjectBadge subject={student.chapter.subject} showLabel={false} />
                            ) : undefined
                        }
                        selected={false}
                        disabled={locked}
                        onPress={() => onChooseChapter?.(student)}
                    />
                ) : (
                    <View style={styles.start}>
                        <Button
                            label={TEXTS.chooseChapter}
                            iconName="book"
                            loading={updating}
                            disabled={locked}
                            onPress={() => onChooseChapter?.(student)}
                        />
                    </View>
                )
            ) : (
                <Stack gap="1">
                    <SelectField
                        label={TEXTS.subjectLabel}
                        placeholder={TEXTS.subjectPlaceholder}
                        items={ordered}
                        getItemId={(subject) => subject.id}
                        getItemLabel={subjectLabel}
                        selectedIds={student.subjectId != null ? [student.subjectId] : []}
                        onChange={(ids) => {
                            const [id] = ids
                            if (id != null) onSelectSubject?.(student, id)
                        }}
                        disabled={locked}
                    />
                    {updating && (
                        <WaitingText size="text-sm" color="text-muted">
                            {TEXTS.updating}
                        </WaitingText>
                    )}
                </Stack>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    grow: { flex: 1, flexShrink: 1 },
    wrap: { flexWrap: "wrap" },
    start: { alignSelf: "flex-start" },
}))
